import React from 'react';
import {
  classNames,
  layoutClassNames,
  LayoutClassNameProps,
  positionClassNames,
  PositionClassNameProps,
  textClassNames,
  TextClassNameProps,
} from './classNames';

export type CardShadow = false | 'none' | 'sm' | 'lg';

export interface CardProps extends React.HTMLAttributes<HTMLElement>, LayoutClassNameProps, PositionClassNameProps {
  as?: React.ElementType;
  shadow?: CardShadow;
  borderless?: boolean;
  fullHeight?: boolean;
  fullWidth?: boolean;
}

export const Card: React.FC<CardProps> = ({
  as: Component = 'div',
  shadow,
  borderless = false,
  fullHeight = false,
  fullWidth = false,
  className,
  children,
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
  bottom,
  end,
  fixed,
  position,
  positionTranslate,
  start,
  top,
  ...props
}) => {
  const shadowClass = shadow && shadow !== 'none' ? `shadow-${shadow}` : undefined;

  return (
    <Component
      className={classNames(
        'card',
        shadowClass,
        borderless ? 'border-0' : undefined,
        fullHeight ? 'h-100' : undefined,
        fullWidth ? 'w-100' : undefined,
        positionClassNames({
          bottom,
          end,
          fixed,
          position,
          positionTranslate,
          start,
          top,
        }),
        layoutClassNames({
          margin,
          marginTop,
          marginEnd,
          marginBottom,
          marginStart,
          marginX,
          marginY,
          padding,
          paddingTop,
          paddingEnd,
          paddingBottom,
          paddingStart,
          paddingX,
          paddingY,
          textAlign,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement>, LayoutClassNameProps {}

export const CardBody: React.FC<CardBodyProps> = ({
  className,
  children,
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
  ...props
}) => (
  <div
    className={classNames(
      'card-body',
      layoutClassNames({
        margin,
        marginTop,
        marginEnd,
        marginBottom,
        marginStart,
        marginX,
        marginY,
        padding,
        paddingTop,
        paddingEnd,
        paddingBottom,
        paddingStart,
        paddingX,
        paddingY,
        textAlign,
      }),
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

export interface CardTitleProps
  extends React.HTMLAttributes<HTMLElement>,
    LayoutClassNameProps,
    TextClassNameProps {
  as?: React.ElementType;
}

export const CardTitle: React.FC<CardTitleProps> = ({
  as: Component = 'h5',
  className,
  children,
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
  textColor,
  textDecoration,
  textReset,
  ...props
}) => (
  <Component
    className={classNames(
      'card-title',
      textClassNames({
        textColor,
        textDecoration,
        textReset,
      }),
      layoutClassNames({
        margin,
        marginTop,
        marginEnd,
        marginBottom,
        marginStart,
        marginX,
        marginY,
        padding,
        paddingTop,
        paddingEnd,
        paddingBottom,
        paddingStart,
        paddingX,
        paddingY,
        textAlign,
      }),
      className,
    )}
    {...props}
  >
    {children}
  </Component>
);

export interface CardSubtitleProps
  extends React.HTMLAttributes<HTMLElement>,
    LayoutClassNameProps,
    TextClassNameProps {
  as?: React.ElementType;
}

export const CardSubtitle: React.FC<CardSubtitleProps> = ({
  as: Component = 'h6',
  className,
  children,
  margin,
  marginTop,
  marginEnd,
  marginBottom,
  marginStart,
  marginX,
  marginY,
  padding,
  paddingTop,
  paddingEnd,
  paddingBottom,
  paddingStart,
  paddingX,
  paddingY,
  textAlign,
  textColor,
  textDecoration,
  textReset,
  ...props
}) => (
  <Component
    className={classNames(
      'card-subtitle',
      textClassNames({
        textColor,
        textDecoration,
        textReset,
      }),
      layoutClassNames({
        margin,
        marginTop,
        marginEnd,
        marginBottom,
        marginStart,
        marginX,
        marginY,
        padding,
        paddingTop,
        paddingEnd,
        paddingBottom,
        paddingStart,
        paddingX,
        paddingY,
        textAlign,
      }),
      className,
    )}
    {...props}
  >
    {children}
  </Component>
);

export default Card;
